import { useState } from "react";
import { X, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "../lib/store";
import { toast } from "sonner";
import { TermsCheckbox } from "./TermsCheckbox";

export const AuthModal = ({ open, onClose }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bentornato!");
      } else {
        if (!accepted) {
          toast.error("Devi accettare Termini e Privacy Policy");
          return;
        }
        await register(form.email, form.password, form.name);
        toast.success("Account creato");
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0b0b0d] border border-[#C9A227]/25 rounded-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-[#C9A227]/15 flex justify-between items-center">
          <div>
            <div className="eyebrow">PULL STATION</div>
            <h3 className="font-display text-2xl font-bold text-white mt-1">
              {mode === "login" ? "Accedi" : "Registrati"}
            </h3>
          </div>
          <button data-testid="auth-close" onClick={onClose} className="p-2 text-white hover:text-[#C9A227]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <button
            data-testid="google-auth-button"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 border border-[#C9A227]/30 hover:border-[#C9A227] rounded-sm py-3 text-sm text-white uppercase tracking-widest font-mono-ps transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-3.5 5.5-6 9.7-6 3.1 0 5.9 1.2 8 3l5.7-5.7C34.9 6.1 29.7 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.6 0 10.6-2.1 14.4-5.6l-6.6-5.4c-2 1.4-4.7 2.4-7.8 2.4-5.2 0-9.6-3.3-11.2-8L6.3 32C9.6 38.5 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.6 5.4C41.2 36.4 44 30.7 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continua con Google
          </button>
          <p className="text-[11px] text-[#6B7280] text-center -mt-1">
            Continuando con Google accetti i <a href="/termini" className="text-[#C9A227]">Termini</a> e la <a href="/privacy" className="text-[#C9A227]">Privacy Policy</a>.
          </p>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#C9A227]/15" />
            <span className="text-[10px] font-mono-ps tracking-widest uppercase text-[#9CA3AF]">oppure</span>
            <div className="flex-1 h-px bg-[#C9A227]/15" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="flex items-center border border-[#C9A227]/25 rounded-sm bg-black">
                <UserIcon size={16} className="text-[#9CA3AF] ml-3" />
                <input
                  data-testid="auth-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome"
                  className="flex-1 bg-transparent px-3 py-3 text-white placeholder:text-[#6B7280] outline-none text-base"
                />
              </div>
            )}
            <div className="flex items-center border border-[#C9A227]/25 rounded-sm bg-black">
              <Mail size={16} className="text-[#9CA3AF] ml-3" />
              <input
                data-testid="auth-email"
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="flex-1 bg-transparent px-3 py-3 text-white placeholder:text-[#6B7280] outline-none text-base"
              />
            </div>
            <div className="flex items-center border border-[#C9A227]/25 rounded-sm bg-black">
              <Lock size={16} className="text-[#9CA3AF] ml-3" />
              <input
                data-testid="auth-password"
                required type="password" minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                className="flex-1 bg-transparent px-3 py-3 text-white placeholder:text-[#6B7280] outline-none text-base"
              />
            </div>
            {mode === "register" && <TermsCheckbox checked={accepted} onChange={setAccepted} testid="auth-accept-terms" />}
            <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-gold w-full justify-center disabled:opacity-60">
              {busy ? "Attendi…" : mode === "login" ? "Accedi" : "Crea account"}
            </button>
          </form>

          <div className="text-center text-sm text-[#9CA3AF]">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              data-testid="auth-switch"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#C9A227] hover:text-[#F3D068] font-semibold"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
